import React from 'react';
import { Card, Row, Col } from 'antd';
import moment from 'moment';
import { Colors } from 'works/legacy/lib/style-helper';
import {
  StatePill,
  Icon,
  Gap,
  Gutter,
  SecondaryButton,
  Text,
} from '@common/components';
import { capitalizeFirstLetter, formatNumberDigits } from '@common/services';
import { PerformWorksShiftActionButton } from '@scheduling/components';
import {
  ShiftGapsByDayQueryCgResponse,
  ShiftsByDayQueryCgResponse,
  TimeOfDay,
} from '@common/graphql/schema';
import { getShiftSourceDisplay } from '@scheduling/services';
import { toHumanTimeFormat } from 'works/legacy/lib/date-helper';

type GapType =
  ShiftGapsByDayQueryCgResponse['shiftGapsByDay']['shiftGaps'][number];

type OpeningType =
  ShiftsByDayQueryCgResponse['shiftsByDay']['shiftGapOpenings'][number];

export function ShiftsCalendarDetailsCard({
  shiftOpening,
  shiftGap,
  showStatePill = false,
  onClick,
  openingCount,
  canSendShift,
  onPressSendShift,
  style,
  titlePrefix = '',
}: {
  shiftOpening?: OpeningType | undefined;
  shiftGap?:
    | Partial<{
        id: string;
        shiftDuration: string | number;
        startDatetime: string;
        endDatetime: string;
        timeZone: string;
        timeOfDay?: TimeOfDay | null | undefined;
        shiftGapTags?: { id: string; name: string }[];
        source?: string | null | undefined;
        organizationUnit: {
          id: string;
          name: string;
          costCenter?: string | null | undefined;
        };
        organizationBusiness: { id: string; name: string };
        role:
          | { id: string; name: string; abbreviation: string }
          | null
          | undefined;
      }>
    | GapType;
  showStatePill?: boolean;
  onClick: (id: string) => void;
  openingCount?: number;
  canSendShift?: boolean;
  onPressSendShift?: (id: string) => void;
  style?: React.CSSProperties;
  titlePrefix?: string;
}) {
  const shift = (shiftOpening?.shiftGap || shiftGap)!;
  const startTime = moment.parseZone(shift.startDatetime).tz(shift.timeZone!);
  const endTime = moment.parseZone(shift.endDatetime).tz(shift.timeZone!);
  const isNightShift = shift.timeOfDay === 'night';

  const worker = shiftOpening?.worksShift?.worker.worksUser.name;
  const humanizedState = shiftOpening?.worksShift
    ? capitalizeFirstLetter(
        shiftOpening?.worksShift.state.toLowerCase?.() || ''
      )
    : capitalizeFirstLetter(shiftOpening?.state?.toLowerCase?.() || '');

  const rate = (
    worker?: NonNullable<OpeningType['worksShift']>['worker']
  ): string | null | undefined => {
    if (worker?.isInternal) {
      return shiftOpening?.worksShift?.formattedPerHourIncentivePay;
    }

    const billingRegularRate = worker?.allowLists?.find(
      (allowList) =>
        allowList?.organizationUnit?.id ===
          shiftOpening?.shiftGap?.organizationUnit?.id &&
        allowList?.role.id === shiftOpening?.shiftGap?.role?.id
    )?.staffingRequestAssignment?.staffingRequest?.billingRegularRate;

    if (!billingRegularRate) {
      return shiftOpening?.worksShift?.formattedPerHourIncentivePay;
    }

    return `$${Number(billingRegularRate).toFixed(2)}`;
  };

  const note = shiftOpening?.worksShift?.workerNote;
  const canApproveReject =
    shiftOpening?.worksShift && shiftOpening?.canApproveReject?.value;
  const hasFooterElements =
    (note?.length || 0) > 0 || canApproveReject || canSendShift;

  const shiftSourceDisplay = getShiftSourceDisplay(shift?.source);
  const sourceInfo = (
    <Gap align="center" size="xs">
      <Icon icon={shiftSourceDisplay.icon} kind="light" />
      <Text>{shiftSourceDisplay.label}</Text>
    </Gap>
  );

  return (
    <>
      <Card
        className="ShiftCalendarCard"
        onClick={() => onClick(shift?.id || '')}
        style={style}
      >
        <Row>
          <Col span={16}>
            <Row align="middle" className="shift-title">
              <Icon
                icon={isNightShift ? 'moon' : 'sun'}
                color={isNightShift ? 'skyRegular' : 'harvestGold'}
              />
              <Gutter size="sm" direction="left">
                <Text underline size="lg" color="elm" weight="medium">
                  {`${titlePrefix}${toHumanTimeFormat(startTime)} - ${toHumanTimeFormat(endTime)} (${formatNumberDigits(shift.shiftDuration)} hrs)`}
                </Text>
              </Gutter>
            </Row>
          </Col>
          <Col span={8}>
            <Row justify="end">
              {shift.shiftGapTags &&
                shift.shiftGapTags.map((tag) => (
                  <div
                    key={tag.id}
                    style={{
                      backgroundColor: Colors.skyLightest,
                      cursor: 'pointer',
                      marginLeft: 8,
                      padding: '0px 12px',
                      borderRadius: 24,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Text weight="medium">{tag?.name}</Text>
                  </div>
                ))}
              {showStatePill && (
                <div style={{ marginLeft: 8 }}>
                  <StatePill color="steel-lighter">
                    <Text weight="medium">{humanizedState}</Text>
                  </StatePill>
                </div>
              )}
            </Row>
          </Col>
        </Row>
        <Row
          justify="space-between"
          style={{ flexWrap: 'nowrap' }}
          align="bottom"
        >
          <Col flex="auto" span={hasFooterElements ? 24 : 18}>
            <Row className="shift-details">
              <Text>Unit: {shift.organizationUnit?.name}</Text>
            </Row>
            <Row className="shift-details">
              <Text>Location: {shift.organizationBusiness?.name}</Text>
            </Row>
            <Row className="shift-details">
              <Text>Role: {shift?.role?.abbreviation}</Text>
            </Row>
            {openingCount !== undefined && openingCount !== null && (
              <Row className="shift-details">
                <Text>Openings: {openingCount}</Text>
              </Row>
            )}
            {worker && (
              <>
                <Row className="shift-details">
                  <Text className="sentry-mask">
                    Worker: {shiftOpening.worksShift?.worker.worksUser.name}
                    {shiftOpening.worksShift?.worker &&
                      rate(shiftOpening?.worksShift?.worker) && (
                        <>
                          {' '}
                          - {rate(shiftOpening?.worksShift?.worker)}
                          /hr
                        </>
                      )}
                  </Text>
                </Row>
                <Row className="shift-details">
                  <Text>
                    Workforce Group:{' '}
                    {shiftOpening.worksShift?.worker?.workforceGroup?.name}
                  </Text>
                </Row>
              </>
            )}
          </Col>
          {!hasFooterElements && <Col>{sourceInfo}</Col>}
        </Row>
        {note && <div className="note">{note}</div>}
        {hasFooterElements && (
          <Row justify="space-between" align="middle">
            <Col className="action-buttons">
              {canSendShift && (
                <SecondaryButton
                  icon={
                    <Icon
                      icon="paperPlane"
                      kind="light"
                      size="md"
                      className="paper-plane-icon"
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onPressSendShift?.(shift.id || '');
                  }}
                >
                  Send Shift
                </SecondaryButton>
              )}
              {canApproveReject && shiftOpening?.worksShift && (
                <>
                  <PerformWorksShiftActionButton
                    worksShiftId={shiftOpening.worksShift.id}
                    type="reject"
                  />
                  <PerformWorksShiftActionButton
                    worksShiftId={shiftOpening.worksShift.id}
                    type="confirm"
                  />
                </>
              )}
            </Col>
            <Col>
              <Gutter size="lg" direction="top" />
              {sourceInfo}
            </Col>
          </Row>
        )}
      </Card>
      <style jsx>{`
        :global(.note) {
          margin-top: 16px;
          background-color: ${Colors.meadowHyperLight};
          padding: 16px;
          width: 100%;
        }
        :global(.action-buttons) {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          margin-top: 16px;
        }
        :global(.ShiftCalendarCard) {
          border: 1px solid ${Colors.steel};
          border-radius: 8px;
          box-shadow: 0 2px 4px rgb(36 35 41 / 8%);
          margin: 0 6px 16px 6px;
          cursor: pointer;
          transition: box-shadow 0.2s ease;
        }
        :global(.ShiftCalendarCard:hover) {
          box-shadow: 0 4px 8px rgb(36 35 41 / 12%);
        }
        :global(.ShiftCalendarCard .shift-details .ant-typography) {
          color: ${Colors.midnightRegular};
        }
        :global(.ShiftCalendarCard .shift-title) {
          padding-bottom: 16px;
        }
        :global(.ShiftCalendarCard) :global(.paper-plane-icon) {
          margin-right: 5px;
        }
      `}</style>
    </>
  );
}
